import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiAlertTriangle, FiCheckCircle } from 'react-icons/fi';
import { QUERY_KEY } from '../../../../../services/queryEndPoint';
import { getAuthToken, decodeAuthTokenPayload, buildLoginRedirectUrl } from '../../../../../helper/helper';
import { useAppSelector } from '../../../../../redux/hooks';
import { selectSelectedLeague } from '../../../../../redux/slice/selectedLeagueSlice';
import { canManageWaiversInLeague } from '../../../../../utils/leagueAccess';
import { fetchMyWaiverStatus } from './waiverSignatureApi';
import CompleteWaiverWizard from './CompleteWaiverWizard';
import AdminWaiverPanel from './AdminWaiverPanel';
import './MyWaiverTab.scss';

interface Props {
  tournamentId: string | null;
  tournamentName?: string | null;
  leagueId: string | null;
}

function formatPlayerRole(role: string | null): string {
  if (!role) return '-';
  return role
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join('-');
}

export default function MyWaiverTab({ tournamentId, tournamentName, leagueId }: Props) {
  const [showWizard, setShowWizard] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const token = getAuthToken();
  const myPlayerId = token ? (decodeAuthTokenPayload(token)?.sub as string | undefined) : undefined;
  const isSignedIn = !!myPlayerId;

  const { data: status, isLoading, isError, error, refetch } = useQuery({
    queryKey: [QUERY_KEY.tournamentWaiverSignature, tournamentId, 'my-waiver-status'],
    queryFn: () => fetchMyWaiverStatus(tournamentId as string),
    enabled: !!tournamentId && isSignedIn,
    // A 403 here always means "not really authenticated" (self-only endpoint, no other 403
    // reason exists) — retrying won't fix that, so fail fast into the redirect below instead of
    // burning a few seconds on React Query's default retries first.
    retry: false,
  });

  // Two distinct "not really logged in" cases land here, both sent to the same place: (1) no
  // token at all (never logged in, or logged out) — checked before the query even runs; (2) a
  // token that decodes fine client-side but the backend rejects as anonymous (expired/invalid —
  // JwtAuthenticationFilter doesn't fail the request outright, it just leaves the caller
  // unauthenticated, so this surfaces as a 403 here, not a 401 the global interceptor would
  // already catch). Sending the player through /login and back is what makes a waiver reminder
  // email link actually work when the session has lapsed since it was sent.
  const returnPath = `${location.pathname}${location.search}`;
  const sessionInvalid = isError && (error as { response?: { status?: number } })?.response?.status === 403;
  useEffect(() => {
    if (!isSignedIn || sessionInvalid) {
      navigate(buildLoginRedirectUrl(returnPath), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, sessionInvalid]);

  const isOnTeam = !!status?.teamSnapshotId;

  const selectedLeague = useAppSelector(selectSelectedLeague);
  const isAdmin = canManageWaiversInLeague(selectedLeague);

  const bannerState = useMemo<'pending' | 'completed' | null>(() => {
    if (!status || !status.waiverRequired) return null;
    return status.signed ? 'completed' : 'pending';
  }, [status]);

  if (!isSignedIn || sessionInvalid) {
    // The effect above is already navigating away — this only renders for the one frame before
    // that completes.
    return (
      <div className="my-waiver-tab">
        <p className="my-waiver-empty">Redirecting to login…</p>
      </div>
    );
  }

  if (isLoading || !tournamentId) {
    return (
      <div className="my-waiver-tab">
        <p className="my-waiver-empty">Loading…</p>
      </div>
    );
  }

  if (showWizard && status) {
    return (
      <CompleteWaiverWizard
        tournamentId={tournamentId}
        tournamentName={tournamentName ?? null}
        leagueId={leagueId}
        waiverId={status.waiverId}
        teamName={status.teamName}
        onClose={() => setShowWizard(false)}
        onSigned={() => {
          setShowWizard(false);
          void refetch();
        }}
      />
    );
  }

  if (!status || !status.waiverRequired) {
    return (
      <div className="my-waiver-tab">
        <p className="my-waiver-empty">This tournament does not require a waiver.</p>
      </div>
    );
  }

  return (
    <div className="my-waiver-tab">
      {bannerState && (
        <div className={`my-waiver-banner my-waiver-banner--${bannerState}`}>
          <div className="my-waiver-banner-text">
            <span className="my-waiver-banner-icon" aria-hidden>
              {bannerState === 'completed' ? <FiCheckCircle size={20} /> : <FiAlertTriangle size={20} />}
            </span>
            <div>
              <p className="my-waiver-banner-title">Waiver required before you can be selected</p>
              <p className="my-waiver-banner-subtitle">
                {!isOnTeam
                  ? `You'll be able to complete this once you're added to a team for ${tournamentName ?? 'this tournament'}.`
                  : status.teamName
                    ? `You've been added to ${status.teamName} for ${tournamentName ?? 'this tournament'}. Takes about a minute.`
                    : `Complete your waiver for ${tournamentName ?? 'this tournament'}. Takes about a minute.`}
              </p>
            </div>
          </div>
          {bannerState === 'completed' ? (
            <span className="my-waiver-banner-badge">Completed</span>
          ) : (
            // Only players actually rostered to a team for this tournament can sign — signing
            // requires a team context, and there's nothing to select them for otherwise.
            isOnTeam && (
              <button type="button" className="my-waiver-btn my-waiver-btn--primary" onClick={() => setShowWizard(true)}>
                Complete Waiver
              </button>
            )
          )}
        </div>
      )}

      {status.teamName && (
        <div className="my-waiver-roster-card">
          <div className="my-waiver-roster-header">
            <div>
              <h2>{status.teamName}</h2>
              <p>{tournamentName ?? '-'}</p>
            </div>
            <span className={`my-waiver-status-pill my-waiver-status-pill--${bannerState}`}>
              {bannerState === 'completed' ? 'Waiver completed' : 'Waiver pending'}
            </span>
          </div>

          <table className="my-waiver-roster-table">
            <thead>
              <tr>
                <th>Player</th>
                <th>Role</th>
                <th>Waiver</th>
              </tr>
            </thead>
            <tbody>
              {status.teammates.map((teammate) => (
                <tr key={teammate.playerId} className={teammate.self ? 'is-self' : ''}>
                  <td>{teammate.playerName || '-'}</td>
                  <td>{formatPlayerRole(teammate.playerRole)}</td>
                  <td>
                    <span className={`my-waiver-status-pill my-waiver-status-pill--${teammate.signed ? 'completed' : 'pending'}`}>
                      {teammate.signed ? 'Signed' : 'Pending'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isAdmin && leagueId && (
        <AdminWaiverPanel tournamentId={tournamentId} leagueId={leagueId} tournamentName={tournamentName} />
      )}
    </div>
  );
}
